import { 
  ref, 
  push, 
  set,
  get,
  onValue,
  remove,
  serverTimestamp,
  query,
  orderByChild
} from 'firebase/database';
import { database } from '../config/firebase';

class CountriesService {
  constructor() {
    this.countriesRef = ref(database, 'vpn_countries');
    this.userSelectionsRef = ref(database, 'user_country_selections');
    this.listeners = new Map();
  }

  // Получить все страны
  async getAllCountries() {
    try {
      const snapshot = await get(this.countriesRef);
      if (snapshot.exists()) {
        const countries = [];
        snapshot.forEach((childSnapshot) => {
          countries.push({
            id: childSnapshot.key,
            ...childSnapshot.val()
          });
        });
        // Сортируем по имени
        return countries.sort((a, b) => (a.name || '').localeCompare(b.name || ''));
      }
      return [];
    } catch (error) {
      console.error('Ошибка получения стран:', error);
      return [];
    }
  }

  // Подписка на изменения списка стран
  subscribeToCountries(callback) {
    const unsubscribe = onValue(this.countriesRef, (snapshot) => {
      const countries = [];
      if (snapshot.exists()) {
        snapshot.forEach((childSnapshot) => {
          countries.push({
            id: childSnapshot.key,
            ...childSnapshot.val()
          });
        });
        // Сортируем по имени
        countries.sort((a, b) => (a.name || '').localeCompare(b.name || ''));
      }
      callback(countries);
    }, (error) => {
      console.error('Ошибка подписки на страны:', error);
      callback([]);
    });

    this.listeners.set('countries', unsubscribe);
    return unsubscribe;
  }

  // Добавить новую страну
  async addCountry(countryName, countryCode = '', flag = '') {
    try {
      if (!countryName || !countryName.trim()) {
        throw new Error('Название страны обязательно');
      }

      // Проверяем, не существует ли уже такая страна
      const existingCountries = await this.getAllCountries();
      const normalizedName = countryName.trim().toLowerCase();
      const exists = existingCountries.some(
        c => c.name && c.name.toLowerCase() === normalizedName
      );

      if (exists) {
        throw new Error('Эта страна уже существует');
      }

      const countryData = {
        name: countryName.trim(),
        code: countryCode.trim() || '',
        flag: flag.trim() || '',
        createdAt: serverTimestamp(),
        addedBy: this.getUserId(),
        votes: 0,
        isActive: true
      };

      await push(this.countriesRef, countryData);
      return { success: true };
    } catch (error) {
      console.error('Ошибка добавления страны:', error);
      return { success: false, error: error.message };
    }
  }

  // Удалить страну
  async deleteCountry(countryId) {
    try {
      const countryRef = ref(database, `vpn_countries/${countryId}`);
      await remove(countryRef);
      return { success: true };
    } catch (error) {
      console.error('Ошибка удаления страны:', error);
      return { success: false, error: error.message };
    }
  }

  // Обновить страну
  async updateCountry(countryId, updates) {
    try {
      const countryRef = ref(database, `vpn_countries/${countryId}`);
      const snapshot = await get(countryRef);
      
      if (!snapshot.exists()) {
        throw new Error('Страна не найдена');
      }

      const currentData = snapshot.val();
      const updatedData = {
        ...currentData,
        ...updates,
        updatedAt: serverTimestamp()
      };

      await set(countryRef, updatedData);
      return { success: true };
    } catch (error) {
      console.error('Ошибка обновления страны:', error);
      return { success: false, error: error.message };
    }
  }

  // Проголосовать за страну (добавить в выбор пользователя)
  async voteForCountry(countryId, countryName) {
    try {
      const userId = this.getUserId();
      const selectionRef = ref(database, `user_country_selections/${userId}/${countryId}`);
      
      const selectionData = {
        countryId,
        countryName,
        timestamp: serverTimestamp(),
        votedAt: Date.now()
      };

      await set(selectionRef, selectionData);

      // Увеличиваем счетчик голосов для страны
      const countryRef = ref(database, `vpn_countries/${countryId}`);
      const snapshot = await get(countryRef);
      
      if (snapshot.exists()) {
        const countryData = snapshot.val();
        const currentVotes = countryData.votes || 0;
        await set(countryRef, {
          ...countryData,
          votes: currentVotes + 1
        });
      }

      return { success: true };
    } catch (error) {
      console.error('Ошибка голосования за страну:', error);
      return { success: false, error: error.message };
    }
  }

  // Убрать голос за страну
  async unvoteForCountry(countryId) {
    try {
      const userId = this.getUserId();
      const selectionRef = ref(database, `user_country_selections/${userId}/${countryId}`);
      
      await remove(selectionRef);

      // Уменьшаем счетчик голосов для страны
      const countryRef = ref(database, `vpn_countries/${countryId}`);
      const snapshot = await get(countryRef);
      
      if (snapshot.exists()) {
        const countryData = snapshot.val();
        const currentVotes = Math.max(0, (countryData.votes || 0) - 1);
        await set(countryRef, {
          ...countryData,
          votes: currentVotes
        });
      }

      return { success: true };
    } catch (error) {
      console.error('Ошибка удаления голоса:', error);
      return { success: false, error: error.message };
    }
  }

  // Получить выбранные пользователем страны
  async getUserSelectedCountries() {
    try {
      const userId = this.getUserId();
      const userSelectionsRef = ref(database, `user_country_selections/${userId}`);
      const snapshot = await get(userSelectionsRef);
      
      if (snapshot.exists()) {
        const selections = [];
        snapshot.forEach((childSnapshot) => {
          selections.push({
            countryId: childSnapshot.key,
            ...childSnapshot.val()
          });
        });
        return selections;
      }
      return [];
    } catch (error) {
      console.error('Ошибка получения выбранных стран:', error);
      return [];
    }
  }

  // Подписка на выбранные пользователем страны
  subscribeToUserSelections(callback) {
    const userId = this.getUserId();
    const userSelectionsRef = ref(database, `user_country_selections/${userId}`);
    
    const unsubscribe = onValue(userSelectionsRef, (snapshot) => {
      const selections = [];
      if (snapshot.exists()) {
        snapshot.forEach((childSnapshot) => {
          selections.push({
            countryId: childSnapshot.key,
            ...childSnapshot.val()
          });
        });
      }
      callback(selections);
    }, (error) => {
      console.error('Ошибка подписки на выбранные страны:', error);
      callback([]);
    });

    this.listeners.set('userSelections', unsubscribe);
    return unsubscribe;
  }

  // Получить уникальный ID пользователя
  getUserId() {
    let userId = localStorage.getItem('chatUserId');
    if (!userId) {
      userId = 'user_' + Date.now() + '_' + Math.random().toString(36).substr(2, 9);
      localStorage.setItem('chatUserId', userId);
    }
    return userId;
  }

  // Отключение от всех подписок
  unsubscribeAll() {
    this.listeners.forEach((unsubscribe) => {
      unsubscribe();
    });
    this.listeners.clear();
  }
}

const countriesService = new CountriesService();
export default countriesService;
